import { useState } from "react";
import { idr } from "../../lib/api";
import { Pencil, Check, X } from "lucide-react";

// Lencana kelas margin (brief owner 18 Sep 2026, batch P) — WARNA TETAP, tanpa
// angka apa pun. Urutan & label PERSIS `akses_modal.kelas_margin()` backend;
// layar hanya memetakan `kode` -> warna, tidak menghitung ambangnya sendiri.
// Dipakai baris SKU (PriceList.jsx) DAN sub-baris unit di bawah — satu peta.
export const KELAS_WARNA = {
  rugi: "bg-rose-50 text-rose-700 border-rose-200",
  tipis: "bg-amber-50 text-amber-700 border-amber-200",
  normal: "bg-slate-100 text-slate-700 border-slate-200",
  tebal: "bg-emerald-50 text-emerald-700 border-emerald-200",
  belum_diketahui: "bg-slate-50 text-slate-400 border-slate-200",
};

const kecil = "h-8 px-2 border border-slate-200 rounded text-xs bg-white";

/**
 * Sub-baris SATU unit (IMEI/SN) di Price List mode "Rinci per IMEI/SN"
 * (brief owner 22 Sep 2026, G2/G3). Komponen MURNI — seluruh keputusan akses
 * datang lewat props dari respons server (`modal_terlihat`, `margin_bentuk`,
 * `pengaturan_harga_unit.kolom_aktif`) dan `can()` pemanggil — supaya bisa
 * dirender apa adanya di tes dengan respons ASLI endpoint.
 *
 * Kolom "Battery Health" dan "Garansi" memakai judul VERBATIM brief Bos. Teks
 * garansi (`garansi_teks`) dan status aktif/habis (`garansi_status`) dihitung
 * SERVER dengan tanggal WIB — layar tidak menghitung tanggal sendiri.
 *
 * Tombol ubah SELALU terlihat (tidak `opacity-0 group-hover`): di tablet/HP
 * tidak ada hover, dan tombol yang hanya muncul saat hover praktis tidak ada
 * (jebakan "fitur ada tapi tak terlihat", CLAUDE.md).
 */
export default function BarisUnit({
  unit, modalTerlihat, marginBentuk, bolehEdit, kolomHargaAktif, onSimpan,
  mulaiEdit = false, kolomAksi = false,
}) {
  const u = unit || {};
  const [edit, setEdit] = useState(!!mulaiEdit);
  const [menyimpan, setMenyimpan] = useState(false);
  const awalForm = {
    battery_health: u.battery_health ?? "",
    garansi_sampai: u.garansi_sampai || "",
    garansi_catatan: u.garansi_catatan || "",
    harga_jual_unit: u.harga_jual_unit ?? "",
  };
  const [f, setF] = useState(awalForm);

  // Hanya medan yang BERUBAH yang dikirim, bersama nilai awal yang terlihat
  // (kunci optimistis di server: suntingan orang lain di sela ini ditolak 409,
  // bukan ditimpa diam-diam). Kosong = hapus (null).
  const simpan = async () => {
    const ubah = {};
    const awal = {};
    const angka = (v) => (v === "" || v == null ? null : Number(v));
    const teks = (v) => ((v || "").trim() ? v.trim() : null);
    const baru = {
      battery_health: angka(f.battery_health),
      garansi_sampai: teks(f.garansi_sampai),
      garansi_catatan: teks(f.garansi_catatan),
      harga_jual_unit: angka(f.harga_jual_unit),
    };
    const lama = {
      battery_health: u.battery_health ?? null,
      garansi_sampai: u.garansi_sampai || null,
      garansi_catatan: u.garansi_catatan || null,
      harga_jual_unit: u.harga_jual_unit ?? null,
    };
    for (const k of Object.keys(baru)) {
      if (k === "harga_jual_unit" && !kolomHargaAktif) continue;
      if (baru[k] !== lama[k]) { ubah[k] = baru[k]; awal[k] = lama[k]; }
    }
    if (Object.keys(ubah).length === 0) { setEdit(false); return; }
    setMenyimpan(true);
    try {
      const ok = await onSimpan?.(u, ubah, awal);
      if (ok !== false) setEdit(false);
    } finally {
      setMenyimpan(false);
    }
  };

  const batal = () => { setF(awalForm); setEdit(false); };
  const sufiks = `${u.id}`;

  return (
    <tr data-testid={`pl-unit-${sufiks}`} className="bg-slate-50/40 text-xs">
      <td className="px-3 py-1.5 text-slate-300 text-right">└</td>
      <td className="px-3 py-1.5" />
      <td className="px-3 py-1.5" />
      <td className="px-3 py-1.5" />
      <td className="px-3 py-1.5 whitespace-nowrap" data-testid={`pl-unit-imei-${sufiks}`}>
        {u.imei && <div className="font-mono text-slate-800">{u.imei}</div>}
        {u.sn && <div className="font-mono text-[11px] text-slate-500">SN {u.sn}</div>}
        {u.cocok_q && <span className="text-[10px] text-emerald-700">cocok pencarian</span>}
      </td>
      <td className="px-3 py-1.5 whitespace-nowrap" data-testid={`pl-unit-bh-sel-${sufiks}`}>
        {edit ? (
          <input type="number" min="0" max="100" step="1" value={f.battery_health}
            onChange={(e) => setF({ ...f, battery_health: e.target.value })}
            data-testid={`pl-unit-bh-${sufiks}`} aria-label="Battery Health (%)"
            className={`${kecil} w-16`} placeholder="%" />
        ) : (u.battery_health != null ? `${u.battery_health}%` : <span className="text-slate-300">—</span>)}
      </td>
      <td className="px-3 py-1.5" data-testid={`pl-unit-garansi-sel-${sufiks}`}>
        {edit ? (
          <div className="flex flex-col gap-1">
            <input type="date" value={f.garansi_sampai}
              onChange={(e) => setF({ ...f, garansi_sampai: e.target.value })}
              data-testid={`pl-unit-garansi-${sufiks}`} aria-label="Garansi berakhir"
              className={kecil} />
            {/* Catatan ini tampil ke SEMUA akun yang membuka Price List (termasuk
                yang tidak berhak melihat modal) — jangan jadi tempat menulis harga. */}
            <input type="text" maxLength={120} value={f.garansi_catatan}
              onChange={(e) => setF({ ...f, garansi_catatan: e.target.value })}
              data-testid={`pl-unit-garansi-catatan-${sufiks}`} aria-label="Catatan garansi"
              title="Terlihat oleh semua yang membuka Price List — jangan isi harga beli/modal"
              placeholder="mis. iBox / garansi toko (bukan harga)" className={kecil} />
          </div>
        ) : u.garansi_teks ? (
          <span className="whitespace-nowrap">
            {u.garansi_teks}
            {u.garansi_status && (
              <span data-testid={`pl-unit-garansi-status-${sufiks}`}
                className={`ml-1 text-[10px] px-1.5 py-0.5 rounded border ${
                  u.garansi_status === "aktif" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                               : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                {u.garansi_status === "aktif" ? "aktif" : "habis"}
              </span>
            )}
          </span>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap" data-testid={`pl-unit-harga-sel-${sufiks}`}>
        {edit && kolomHargaAktif ? (
          <input type="number" min="0" step="1000" value={f.harga_jual_unit}
            onChange={(e) => setF({ ...f, harga_jual_unit: e.target.value })}
            data-testid={`pl-unit-harga-${sufiks}`} aria-label="Harga jual per IMEI"
            placeholder="kosong = harga produk" className={`${kecil} w-32 text-right`} />
        ) : (
          <>
            {idr(u.harga_jual_efektif)}
            {u.harga_sumber === "unit" && (
              <span data-testid={`pl-unit-harga-khusus-${sufiks}`}
                className="block text-[10px] text-violet-700">harga per IMEI</span>
            )}
          </>
        )}
      </td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        {u.status === "rusak"
          ? <span className="text-[10px] text-rose-600" title="unit rusak, tidak layak jual">rusak</span>
          : <span className="text-[10px] text-slate-400">tersedia</span>}
      </td>
      <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{u.gudang || "—"}</td>
      <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">
        {u.cabang || "—"}{u.cabang_via && <span className="ml-1 text-[10px] text-sky-700">{u.cabang_via}</span>}
      </td>
      {modalTerlihat && (
        <td className="px-3 py-1.5 text-right whitespace-nowrap" data-testid={`pl-unit-modal-${sufiks}`}
          title="Modal IMEI = dasar HPP unit ini (bisa beda dari Modal/Unit rata-rata SKU)">
          {u.modal == null ? <span className="text-slate-300">—</span> : idr(u.modal)}
        </td>
      )}
      {marginBentuk === "persen" && (
        <td className="px-3 py-1.5 text-right whitespace-nowrap" data-testid={`pl-unit-margin-${sufiks}`}>
          {u.margin_persen == null
            ? <span className="text-slate-300" title="modal belum diketahui — bukan berarti untung">—</span>
            : <span className={u.rugi ? "text-rose-600 font-semibold" : ""}>{u.margin_persen.toFixed(1)}%</span>}
        </td>
      )}
      {marginBentuk === "kelas" && (
        <td className="px-3 py-1.5 text-right whitespace-nowrap" data-testid={`pl-unit-margin-${sufiks}`}>
          <span data-testid={`pl-unit-kelas-${sufiks}`}
            className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
              KELAS_WARNA[u.margin_kelas?.kode] || KELAS_WARNA.belum_diketahui}`}>
            {u.margin_kelas?.kode === "belum_diketahui" ? "—" : (u.margin_kelas?.label || "—")}
          </span>
        </td>
      )}
      {kolomAksi && (
        <td className="px-2 py-1.5 whitespace-nowrap text-right">
          {bolehEdit && !edit && (
            <button type="button" onClick={() => { setF(awalForm); setEdit(true); }}
              data-testid={`pl-unit-ubah-${sufiks}`}
              title={`Isi Battery Health / Garansi${kolomHargaAktif ? " / harga per IMEI" : ""} unit ini`}
              className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-200 text-slate-600 hover:bg-white">
              <Pencil className="w-3.5 h-3.5" /> Ubah
            </button>
          )}
          {bolehEdit && edit && (
            <span className="inline-flex gap-1">
              <button type="button" onClick={simpan} disabled={menyimpan}
                data-testid={`pl-unit-simpan-${sufiks}`} title="Simpan"
                className="inline-flex items-center h-7 px-2 rounded bg-emerald-600 text-white disabled:opacity-60">
                <Check className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={batal} disabled={menyimpan}
                data-testid={`pl-unit-batal-${sufiks}`} title="Batal"
                className="inline-flex items-center h-7 px-2 rounded border border-slate-200 text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
        </td>
      )}
    </tr>
  );
}
